import React, { useState, useEffect } from 'react';
import {
  Dialog,
  TextField,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton
} from '@material-ui/core';
import {
  QuestionAnswer,
  Category,
  Close,
  Add,
  Delete
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import { getActiveTags, createTag } from '../../services/tagService_new';
import './style.scss';

const QnAUploadForm = ({ open, onClose, editingQnA, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    requiredRank: '',
    questions: [{ question: '', answer: '' }],
    tags: [],
    isActive: true,
    order: 0
  });

  const [uploading, setUploading] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');

  const ranks = [
    { value: 'basic', label: 'Basic' },
    { value: 'team_member', label: 'Team Member' },
    { value: 'premier', label: 'Premier' },
    { value: 'silver', label: 'Silver' },
    { value: 'gold', label: 'Gold' },
    { value: 'platinum', label: 'Platinum' },
    { value: 'diamond', label: 'Diamond' },
    { value: 'blue_diamond', label: 'Blue Diamond' },
    { value: 'presidential_diamond', label: 'Presidential Diamond' }
  ];

  const categories = [
    'Întrebări frecvente',
    'Probleme tehnice',
    'Utilizare produse',
    'Comenzi și livrări',
    'Cont și profil',
    'Business și vânzări',
    'Suport general'
  ];

  useEffect(() => {
    if (open) {
      loadTags();
      if (editingQnA) {
        setFormData({
          title: editingQnA.title || '',
          description: editingQnA.description || '',
          category: editingQnA.category || '',
          requiredRank: editingQnA.requiredRank || '',
          questions: editingQnA.questions || [{ question: '', answer: '' }],
          tags: editingQnA.tags || [],
          isActive: editingQnA.isActive !== undefined ? editingQnA.isActive : true,
          order: editingQnA.order || 0
        });
      } else {
        resetForm();
      }
    }
  }, [open, editingQnA]);

  const loadTags = async () => {
    try {
      console.log('Loading tags...');
      const tags = await getActiveTags();
      console.log('Tags received:', tags);
      
      if (Array.isArray(tags)) {
        setAvailableTags(tags);
        console.log('Tags set successfully:', tags.length, 'tags');
      } else {
        console.warn('Tags response is not an array:', tags);
        setAvailableTags([]);
      }
    } catch (error) {
      console.error('Error loading tags:', error);
      setAvailableTags([]);
      toast.error('Eroare la încărcarea tag-urilor. Se va folosi lista goală.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      requiredRank: '',
      questions: [{ question: '', answer: '' }],
      tags: [],
      isActive: true,
      order: 0
    });
    setNewTagName('');
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index][field] = value;
    setFormData(prev => ({ ...prev, questions: newQuestions }));
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, { question: '', answer: '' }]
    }));
  };

  const removeQuestion = (index) => {
    if (formData.questions.length > 1) {
      const newQuestions = formData.questions.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, questions: newQuestions }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Titlul este obligatoriu');
      return;
    }

    if (formData.questions.length === 0 || !formData.questions[0].question.trim()) {
      toast.error('Te rugăm să adaugi cel puțin o întrebare');
      return;
    }

    setUploading(true);

    try {
      const qnaData = {
        ...formData,
        id: editingQnA?.id || `qna_${Date.now()}`
      };

      await onSave(qnaData);
      toast.success(editingQnA ? 'Q&A actualizat cu succes!' : 'Q&A adăugat cu succes!');
      onClose();
    } catch (error) {
      console.error('Error saving Q&A:', error);
      toast.error('Eroare la salvarea Q&A-ului');
    } finally {
      setUploading(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTagName?.trim()) {
      toast.warning('Introduceți numele pentru tag');
      return;
    }

    try {
      console.log('Creating new tag:', newTagName.trim());
      const newTag = await createTag({
        name: newTagName.trim(),
        category: 'qna',
        isActive: true
      });

      if (newTag && newTag.id) {
        setAvailableTags(prev => [...prev, newTag]);
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag.name]
        }));
        setNewTagName('');
        toast.success('Tag adăugat cu succes!');
      } else {
        throw new Error('Tag creation returned invalid response');
      }
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('Eroare la adăugarea tag-ului: ' + (error.message || 'Eroare necunoscută'));
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      className="qna-upload-dialog"
    >
      <div className="glass-dialog-container">
        <div className="glass-dialog-header">
          <div className="header-icon-wrapper">
            <QuestionAnswer className="header-icon" />
          </div>
          <div className="header-content">
            <h2 className="dialog-title">
              {editingQnA ? 'Editează Q&A' : 'Adaugă Q&A Nou'}
            </h2>
            <p className="dialog-subtitle">
              Creează și configurează secțiuni de întrebări și răspunsuri
            </p>
          </div>
          <button 
            className="close-button-modern" 
            onClick={onClose}
          >
            <Close />
          </button>
        </div>

        <div className="glass-dialog-content">
          <div className="form-sections">
            {/* Basic Information */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card className="glass-card">
                  <CardContent>
                    <Typography variant="h6" className="section-title">
                      <Category className="section-icon" />
                      Informații de Bază
                    </Typography>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <TextField
                          fullWidth
                          label="Titlu *"
                          value={formData.title}
                          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                          className="glass-input"
                          disabled={uploading}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Descriere"
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          className="glass-input"
                          disabled={uploading}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <select
                          className="glass-select"
                          value={formData.category}
                          onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                          disabled={uploading}
                        >
                          <option value="">Selectează categoria</option>
                          {categories.map(category => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <select
                          className="glass-select"
                          value={formData.requiredRank}
                          onChange={(e) => setFormData(prev => ({ ...prev, requiredRank: e.target.value }))}
                          disabled={uploading}
                        >
                          <option value="">Selectează rangul</option>
                          {ranks.map(rank => (
                            <option key={rank.value} value={rank.value}>
                              {rank.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* Questions & Answers */}
              <Grid item xs={12}>
                <Card className="glass-card">
                  <CardContent>
                    <div className="section-header">
                      <Typography variant="h6" className="section-title">
                        <QuestionAnswer className="section-icon" />
                        Întrebări și Răspunsuri
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={addQuestion}
                        className="add-question-btn"
                        disabled={uploading}
                      >
                        Adaugă întrebare
                      </Button>
                    </div>
                    
                    <div className="questions-section">
                      {formData.questions.map((qa, index) => (
                        <div key={index} className="question-item">
                          <div className="question-header">
                            <Typography variant="subtitle1" className="question-number">
                              Întrebarea {index + 1}
                            </Typography>
                            {formData.questions.length > 1 && (
                              <IconButton
                                onClick={() => removeQuestion(index)}
                                className="remove-question-btn"
                                disabled={uploading}
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </div>
                          
                          <div className="form-row">
                            <div className="form-group">
                              <TextField
                                fullWidth
                                label="Întrebare *"
                                value={qa.question}
                                onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                                className="glass-input"
                                disabled={uploading}
                                multiline
                                rows={2}
                              />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <TextField
                                fullWidth
                                label="Răspuns *"
                                value={qa.answer}
                                onChange={(e) => handleQuestionChange(index, 'answer', e.target.value)}
                                className="glass-input"
                                disabled={uploading}
                                multiline
                                rows={4}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* Tags */}
              <Grid item xs={12}>
                <Card className="glass-card">
                  <CardContent>
                    <Typography variant="h6" className="section-title">
                      Tag-uri
                    </Typography>
                    
                    <div className="tags-section">
                      <div className="available-tags">
                        <Typography variant="subtitle2" gutterBottom>
                          Tag-uri disponibile:
                        </Typography>
                        <div className="tags-grid">
                          {availableTags.map((tag) => (
                            <Chip
                              key={tag.id}
                              label={tag.name}
                              clickable
                              onClick={() => {
                                if (!formData.tags.includes(tag.name)) {
                                  setFormData(prev => ({
                                    ...prev,
                                    tags: [...prev.tags, tag.name]
                                  }));
                                }
                              }}
                              className={formData.tags.includes(tag.name) ? 'tag-selected' : 'tag-available'}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="selected-tags">
                        <Typography variant="subtitle2" gutterBottom>
                          Tag-uri selectate:
                        </Typography>
                        <div className="tags-grid">
                          {formData.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              onDelete={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  tags: prev.tags.filter(t => t !== tag)
                                }));
                              }}
                              className="tag-selected"
                            />
                          ))}
                        </div>
                      </div>

                      <div className="add-tag-section">
                        <div className="add-tag-input">
                          <TextField
                            size="small"
                            label="Adaugă tag nou"
                            value={newTagName}
                            onChange={(e) => setNewTagName(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddTag();
                              }
                            }}
                            disabled={uploading}
                          />
                          <Button
                            onClick={handleAddTag}
                            disabled={!newTagName.trim() || uploading}
                            className="add-tag-btn"
                          >
                            Adaugă
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </div>
        </div>

        <div className="glass-dialog-footer">
          <button 
            onClick={onClose} 
            disabled={uploading}
            className="cancel-button-modern"
          >
            Anulează
          </button>
          <button 
            onClick={handleSubmit} 
            disabled={uploading || !formData.title.trim() || !formData.questions[0]?.question.trim()}
            className="save-button-modern"
          >
            {uploading ? 'Se salvează...' : (editingQnA ? 'Actualizează' : 'Salvează')}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default QnAUploadForm;
